import { validateSignalsForNewPanel } from "@/shared/domain/workspaces/signalValidation";
import { WorkspaceBuilder } from "@/shared/domain/workspaces/WorkspaceBuilder";
import { Goto } from "@/shared/navigation";

import { ApprovalMode } from "../approvalModes";
import type { ClientToolDeps, ClientToolExecutionResult } from "../ClientTools";
import { createClientTool, toolError, toolSuccess } from "../ClientTools";

import {
  fetchEntries,
  isStringArray,
  MAX_MESSAGE_PATHS_PER_CALL,
  runPanelMutation,
} from "./workspaceActions";

// ---------------------------------------------------------------------------
// Input types
// ---------------------------------------------------------------------------

export interface OpenPanelInput {
  message_path_ids?: string[];
  /**
   * Legacy single-id form. Not in the published schema; accepted so a tool
   * call issued against the old schema still executes after a deploy.
   */
  message_path_id?: string;
  /** Timestamp in nanoseconds since Unix epoch. */
  timestamp?: number;
  placement?: {
    relative_to_panel_id: string;
    orientation: "top" | "right" | "bottom" | "left";
  };
}

// ---------------------------------------------------------------------------
// Execute
// ---------------------------------------------------------------------------

async function execute(
  input: OpenPanelInput,
  deps?: ClientToolDeps,
): Promise<ClientToolExecutionResult> {
  if (!deps?.goto) {
    return toolError("Navigation is not available");
  }
  if (!deps.topicService) {
    return toolError("Topic service is not available");
  }

  if (
    input.message_path_ids !== undefined &&
    !isStringArray(input.message_path_ids)
  ) {
    return toolError(
      "message_path_ids must be an array of message path id strings",
    );
  }

  let messagePathIds: string[] = [];
  if (input.message_path_ids && input.message_path_ids.length > 0) {
    messagePathIds = input.message_path_ids;
  } else if (input.message_path_id) {
    messagePathIds = [input.message_path_id];
  }

  if (messagePathIds.length === 0) {
    return toolError("message_path_ids is required");
  }
  if (messagePathIds.length > MAX_MESSAGE_PATHS_PER_CALL) {
    return toolError(
      `At most ${MAX_MESSAGE_PATHS_PER_CALL} message paths may be opened in one call.`,
    );
  }

  try {
    const { entries, invalid } = await fetchEntries(
      deps.topicService,
      messagePathIds,
    );
    if (invalid.length > 0) {
      return toolError("Some message paths could not be loaded.", {
        invalid_paths: invalid,
      });
    }
    const timestamp =
      input.timestamp !== undefined ? BigInt(input.timestamp) : undefined;

    if (deps.isOnVisualizerPage?.()) {
      // Falling through to the deep link here would replace the user's whole
      // workspace with a single-panel layout; a missing publish dep is a
      // wiring bug and must fail loudly instead.
      if (!deps.publishWorkspaceAction) {
        return toolError("Workspace actions are not available");
      }
      return runPanelMutation(
        deps.publishWorkspaceAction,
        "open_panel",
        (onResult) => ({
          type: "create_panel",
          payload: {
            entries,
            timestamp,
            placement: input.placement
              ? {
                  relativeToPanelId: input.placement.relative_to_panel_id,
                  orientation: input.placement.orientation,
                }
              : undefined,
            onResult,
          },
        }),
        { opened_in: "current_visualizer" },
      );
    }

    return openViaDeepLink(deps, entries, timestamp, input.placement);
  } catch (err) {
    return toolError(err instanceof Error ? err.message : String(err));
  }
}

/**
 * Off-visualizer-page path: navigate to the visualizer with a deep-link
 * layout describing the panel. Placement is only meaningful relative to an
 * existing workspace, so it is ignored here (flagged in the response).
 */
function openViaDeepLink(
  deps: ClientToolDeps,
  entries: Awaited<ReturnType<typeof fetchEntries>>["entries"],
  timestamp: bigint | undefined,
  placement: OpenPanelInput["placement"],
): ClientToolExecutionResult {
  // The same record-level rules the on-page handler applies (type consensus,
  // duplicates, acceptance, same-topic, map pairing incl. sibling bundling),
  // so the model observes one behavior regardless of which page it runs on.
  const validation = validateSignalsForNewPanel(entries);
  if (!validation.ok) {
    return toolError(
      validation.error,
      validation.invalid.length > 0
        ? {
            invalid_paths: validation.invalid.map((path) => ({
              message_path_id: path.messagePathId,
              reason: path.reason,
            })),
          }
        : {},
    );
  }
  const { panelType, entries: signals } = validation;

  const panelId = crypto.randomUUID();
  const layout = new WorkspaceBuilder()
    .addMultiSignalPanel({
      type: panelType,
      id: panelId,
      signals: signals.map((entry) => ({
        topicRecord: entry.topic,
        messagePathRecord: entry.messagePath,
      })),
    })
    .build();

  const files = [
    ...new Set(signals.map((entry) => entry.topic.association.association_id)),
  ];

  deps.goto.visualize({
    dataSource: { files },
    params: { layout, t: timestamp },
  });

  return toolSuccess("open_panel", {
    navigated_to: Goto.route.Visualize,
    panel_id: panelId,
    ...(placement
      ? {
          placement_ignored:
            "Placement only applies when already on the visualizer page.",
        }
      : {}),
  });
}

// ---------------------------------------------------------------------------
// Tool module export
// ---------------------------------------------------------------------------

export const tool = createClientTool<OpenPanelInput>({
  name: "client_visualize_open_panel",
  description: `Open one or more message paths together in a single new visualizer panel (plot, image, log, etc. based on data type). Group related signals into one call to see them on one panel — e.g. an IMU's gyro x, y, and z as three series on one plot. All message paths in a call must visualize as the same panel type; use separate calls for separate panels. If the user is not already on the visualizer page, navigates there first. Optionally seeks the timeline to a specific timestamp, and optionally places the new panel relative to an existing panel. Returns the created panel_id for later add/remove calls. On failure nothing is opened and the error explains why, with per-path reasons where applicable — adjust the request and retry.`,
  input_schema: {
    type: "object",
    properties: {
      message_path_ids: {
        type: "array",
        items: { type: "string" },
        minItems: 1,
        maxItems: MAX_MESSAGE_PATHS_PER_CALL,
        description:
          "Message path IDs (e.g., ['mp_abc123']). Each identifies a data stream; all must visualize as the same panel type. Group signals that belong together (same unit or sensor) into one call.",
      },
      timestamp: {
        type: "integer",
        description:
          "Timestamp in nanoseconds since Unix epoch. Optional — seeks the timer after opening.",
      },
      placement: {
        type: "object",
        properties: {
          relative_to_panel_id: {
            type: "string",
            description:
              "Id of an existing panel to place the new panel next to. Panel ids are visible in the visualizer state context.",
          },
          orientation: {
            type: "string",
            enum: ["top", "right", "bottom", "left"],
            description:
              "Which side of the reference panel to place the new panel on.",
          },
        },
        required: ["relative_to_panel_id", "orientation"],
        additionalProperties: false,
        description:
          "Optional. Place the new panel relative to an existing panel instead of the default position. Only applies when the user is already on the visualizer page.",
      },
    },
    required: ["message_path_ids"],
    additionalProperties: false,
  },
  execute,
  defaultApprovalMode: ApprovalMode.FOLLOW_AUTO,
  allowableApprovalModes: [
    ApprovalMode.REQUIRE_APPROVAL,
    ApprovalMode.FOLLOW_AUTO,
    ApprovalMode.AUTO_EXECUTE,
  ],
  displayName: "Open Panel",
  displayDescription: "Opens a data panel in the visualizer.",
});
